
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { AIStaffMember } from "@/lib/types";
import { useHiringStages } from "./useHiringStages";
import { useAIStaffCreation } from "./useAIStaffCreation";
import { useHiringNavigation } from "./useHiringNavigation";

export function useHiringProcess() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null);
  
  const { 
    currentStage, 
    setCurrentStage, 
    progress,
    hiringStages,
    markStageComplete 
  } = useHiringStages();
  
  const {
    isSubmitting,
    aiStaff,
    createAIStaff,
    fetchAIStaff
  } = useAIStaffCreation();
  
  const { 
    handleComplete 
  } = useHiringNavigation();

  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true') {
      toast({
        title: "Success!",
        description: "Your AI assistant has been created.",
      });
      
      setCurrentStage(2); // Set to onboarding stage (previously 3)
      
      fetchAIStaff();
    } else if (canceled === 'true') {
      toast({
        title: "Process Canceled",
        description: "You can try again when you're ready.",
        variant: "destructive",
      });
    }
  }, [searchParams, toast, fetchAIStaff]);

  const handleNextStage = async () => {
    if (currentStage < hiringStages.length - 1) {
      if (currentStage === 0 && selectedCandidate === null) {
        toast({
          title: "Selection required",
          description: "Please select an AI assistant to continue.",
          variant: "destructive",
        });
        return;
      }
      
      markStageComplete(currentStage);
      
      // If moving from Interview to Onboarding, create the AI staff
      if (currentStage === 1) {
        const createdStaff = await createAIStaff(selectedCandidate);
        if (createdStaff) {
          setCurrentStage(currentStage + 1);
        }
      } else {
        setCurrentStage(currentStage + 1);
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousStage = () => {
    if (currentStage > 0) {
      setCurrentStage(currentStage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectCandidate = (candidateId: number) => {
    const validMarketingId = 1; // Marketing assistant ID
    if (candidateId === validMarketingId) {
      setSelectedCandidate(candidateId);
    } else {
      toast({
        title: "Coming Soon",
        description: "This AI assistant type will be available soon.",
        variant: "default",
      });
    }
  };

  return {
    currentStage,
    progress,
    isSubmitting,
    aiStaff,
    selectedCandidate,
    hiringStages,
    handleNextStage,
    handlePreviousStage,
    handleSelectCandidate,
    handleComplete,
    fetchAIStaff
  };
}
